import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable, split, } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-sse";
import { print } from "graphql";
const isConflictExtensions = (ext) => {
    return ext?.code === "CONFLICT";
};
export const createApolloClient = ({ authClient, httpEndpoint, sseEndpoint, cache, onConflict, }) => {
    const httpLink = new HttpLink({ uri: httpEndpoint });
    const authLink = setContext(async (_, { headers }) => {
        const accessToken = await authClient.getValidAccessToken();
        return {
            headers: {
                ...(headers ?? {}),
                authorization: accessToken ? `Bearer ${accessToken}` : "",
            },
        };
    });
    let isRefreshing = false;
    let pendingRequests = [];
    const addPendingRequest = (cb) => {
        pendingRequests.push(cb);
    };
    const resolvePendingRequests = async (accessToken) => {
        const newAccess = accessToken ?? (await authClient.getValidAccessToken({ minTtlSeconds: 0 }));
        pendingRequests.forEach((cb) => cb(newAccess ?? null));
        pendingRequests = [];
    };
    const rejectPendingRequests = () => {
        pendingRequests.forEach((cb) => cb(null));
        pendingRequests = [];
    };
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (onConflict && graphQLErrors) {
            for (const error of graphQLErrors) {
                const ext = error.extensions;
                if (isConflictExtensions(ext)) {
                    onConflict(ext);
                }
            }
        }
        const ne = networkError;
        const statusCode = ne?.statusCode;
        const isUnauthorizedMsg = Boolean(graphQLErrors?.some((e) => e.message === "Unauthorized"));
        const isUnauthenticatedCode = Boolean(graphQLErrors?.some((e) => e.extensions?.code === "UNAUTHENTICATED"));
        const isUserNotFoundMsg = Boolean(graphQLErrors?.some((e) => e.message === "Context creation failed: User not found"));
        const unauthorized = Boolean(statusCode === 401 || isUnauthorizedMsg || isUnauthenticatedCode || (statusCode === 400 && isUserNotFoundMsg));
        if (!unauthorized) {
            return;
        }
        if (operation.operationName === "RefreshToken") {
            void authClient.clearTokens("refresh_operation_failed");
            return;
        }
        if (isRefreshing) {
            return new Observable((observer) => {
                addPendingRequest(async (token) => {
                    if (!token) {
                        observer.error(networkError ?? new Error("Unauthorized"));
                        return;
                    }
                    operation.setContext(({ headers = {} }) => ({
                        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
                    }));
                    forward(operation).subscribe({
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                    });
                });
            });
        }
        isRefreshing = true;
        return new Observable((observer) => {
            addPendingRequest(async (token) => {
                if (!token) {
                    observer.error(networkError ?? new Error("Unauthorized"));
                    return;
                }
                operation.setContext(({ headers = {} }) => ({
                    headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
                }));
                forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                });
            });
            authClient
                .refreshAccessToken()
                .then(async (result) => {
                isRefreshing = false;
                if (!result?.accessToken) {
                    rejectPendingRequests();
                    return;
                }
                await resolvePendingRequests(result.accessToken);
            })
                .catch(() => {
                isRefreshing = false;
                rejectPendingRequests();
            });
        });
    });
    const createSseApolloLink = () => {
        if (!sseEndpoint || typeof window === "undefined") {
            return null;
        }
        const buildAuthHeader = (token) => {
            if (!token) {
                return {};
            }
            return { Authorization: `Bearer ${token}` };
        };
        return new ApolloLink((operation) => {
            return new Observable((observer) => {
                const sseClient = createClient({
                    url: sseEndpoint,
                    headers: async () => {
                        const accessToken = await authClient.getValidAccessToken({ minTtlSeconds: 30 });
                        return buildAuthHeader(accessToken);
                    },
                });
                const dispose = sseClient.subscribe({
                    operationName: operation.operationName,
                    query: print(operation.query),
                    variables: operation.variables,
                }, {
                    next: (value) => observer.next?.(value),
                    error: (err) => observer.error?.(err),
                    complete: () => observer.complete?.(),
                });
                return () => {
                    dispose();
                    sseClient.dispose();
                };
            });
        });
    };
    const httpAuthedLink = ApolloLink.from([authLink, errorLink, httpLink]);
    const sseLink = createSseApolloLink();
    return new ApolloClient({
        link: sseLink
            ? split((operation) => {
                const definition = getMainDefinition(operation.query);
                return definition.kind === "OperationDefinition" && definition.operation === "subscription";
            }, sseLink, httpAuthedLink)
            : httpAuthedLink,
        cache: cache ?? new InMemoryCache(),
    });
};
